import { ChangeDetectorRef, Component, inject, OnInit, PLATFORM_ID } from '@angular/core'
import { isPlatformBrowser } from '@angular/common'

import { ApiOmnichannelMetricsService } from '../data/services/api/api-omnichannel-metrics.service'
import { OmnichannelMetricsResponse, TopReferrer, SellerProspectRow, SellerClientRow, SellerProspectDetail, SellerProspectItem } from '../data/interfaces/omnichannel-metrics.interface'

//* Paleta de charts (tokens del design system + acentos). Fuente única de color del dashboard.
const PALETTE = {
  navy: '#202835',
  green: '#00c781',
  amber: '#f97316',
  blue: '#3b82f6',
  red: '#d21313',
  indigo: '#6366f1',
  gray: '#9ca3af',
}

interface KpiCard {
  key: string
  label: string
  value: string | number
  icon: string
  accent: 'green' | 'amber' | 'red' | 'blue' | 'indigo'
}

@Component({
  selector: 'app-omnichannel-metrics',
  templateUrl: './omnichannel-metrics.component.html',
  styleUrls: ['./omnichannel-metrics.component.scss'],
})
export class OmnichannelMetricsComponent implements OnInit {
  loading = true
  loadError = false

  kpiCards: KpiCard[] = []

  //* Origen de captación (lista de barras con etiqueta — misma familia visual que el desempeño)
  acquisition: { label: string; count: number; color: string; fill: string }[] = []
  hasAcquisition = false
  acqMax = 1
  acqTotal = 0

  //* Embudo de conversión (doughnut)
  conversionData: any = null
  conversionOptions: any = null
  hasConversion = false
  conversionHeight = '300px'

  //* Prospectos nuevos por mes (barras CSS que llenan el alto de la card)
  arrivalsMonths: { label: string; count: number }[] = []
  hasArrivals = false
  arrivalsMax = 1

  //* Top referidores (lista de barras con etiqueta)
  hasReferrers = false
  topReferrers: TopReferrer[] = []
  refMax = 1

  //* Desempeño por vendedor (barras con etiqueta).
  sellerProspects: SellerProspectRow[] = []
  sellerClients: SellerClientRow[] = []
  hasSellerProspects = false
  hasSellerClients = false
  spMax = 1 //* máximo de prospectos (escala el ancho de las barras)
  scMax = 1 //* máximo de clientes (idem)

  //* Modal de detalle de prospectos por vendedor (tab por vendedor).
  sellerProspectDetails: SellerProspectDetail[] = []
  detailVisible = false
  detailActiveIndex = 0

  private platformId = inject(PLATFORM_ID)

  constructor(private api: ApiOmnichannelMetricsService, private cd: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.api.getMetrics().subscribe({
      next: (res) => {
        const m = ((res as any)?.data ?? res) as OmnichannelMetricsResponse
        this.buildKpis(m)
        this.buildAcquisition(m)
        this.buildReferrers(m)
        this.buildArrivals(m)
        this.buildSellerData(m)
        if (isPlatformBrowser(this.platformId)) this.buildCharts(m)
        this.loading = false
        this.cd.markForCheck()
      },
      error: () => {
        this.loading = false
        this.loadError = true
        this.cd.markForCheck()
      },
    })
  }

  private buildKpis(m: OmnichannelMetricsResponse): void {
    const k = m?.kpis ?? ({} as any)
    this.kpiCards = [
      { key: 'clientes', label: 'Clientes', value: k.clientes ?? 0, icon: 'fa-solid fa-user-check', accent: 'green' },
      { key: 'prospectos', label: 'Prospectos', value: k.prospectos ?? 0, icon: 'fa-solid fa-user-clock', accent: 'amber' },
      { key: 'referred', label: 'Referidos', value: k.referred ?? 0, icon: 'fa-solid fa-user-plus', accent: 'indigo' },
      { key: 'won', label: 'Ganados', value: k.won ?? 0, icon: 'fa-solid fa-trophy', accent: 'green' },
      { key: 'lost', label: 'Perdidos', value: k.lost ?? 0, icon: 'fa-solid fa-user-xmark', accent: 'red' },
      { key: 'winRate', label: 'Tasa de conversión', value: `${k.winRate ?? 0}%`, icon: 'fa-solid fa-percent', accent: 'blue' },
    ]
  }

  private buildCharts(m: OmnichannelMetricsResponse): void {
    const docStyle = getComputedStyle(document.documentElement)
    const textColor = docStyle.getPropertyValue('--p-text-color')?.trim() || PALETTE.navy
    const border = docStyle.getPropertyValue('--p-content-border-color')?.trim() || 'rgba(32,40,53,0.08)'

    // ── Embudo de conversión (polar area, conteo centrado en el perímetro) ───
    const conv = m?.byConversionStatus ?? []
    this.hasConversion = conv.some((c) => c.count > 0)
    const convFill: Record<string, string> = { in_funnel: 'rgba(249,115,22,0.65)', in_origination: 'rgba(59,130,246,0.65)', won: 'rgba(0,199,129,0.65)', lost: 'rgba(210,19,19,0.6)' }
    const convCounts = conv.map((c) => c.count)
    this.conversionData = {
      labels: conv.map((c) => c.label),
      datasets: [{ data: convCounts, backgroundColor: conv.map((c) => convFill[c.status] ?? 'rgba(156,163,175,0.6)'), borderColor: '#fff', borderWidth: 2 }],
    }
    this.conversionOptions = {
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'bottom', labels: { color: textColor, usePointStyle: true, pointStyle: 'circle', padding: 16, font: { size: 12 } } },
        tooltip: { backgroundColor: 'rgba(32,40,53,0.92)', titleColor: '#fff', bodyColor: '#fff', padding: 10, cornerRadius: 8, callbacks: { label: (ctx: any) => ` ${ctx.label}: ${ctx.parsed}` } },
      },
      scales: {
        r: {
          beginAtZero: true,
          grid: { color: border },
          angleLines: { color: border },
          ticks: { display: false },
          //* El número exacto va centrado en el perímetro de cada segmento (legible sin hover).
          pointLabels: { display: true, centerPointLabels: true, color: PALETTE.navy, font: { size: 13, weight: 600 }, callback: (_label: any, i: number) => String(convCounts[i] ?? 0) },
        },
      },
    }

  }

  /** Prospectos por mes como barras CSS (llenan el alto de la card por construcción; no depende del browser). */
  private buildArrivals(m: OmnichannelMetricsResponse): void {
    this.arrivalsMonths = this.fillLastMonths(m?.arrivalsByMonth ?? [], 12)
    this.hasArrivals = this.arrivalsMonths.some((x) => x.count > 0)
    this.arrivalsMax = Math.max(1, ...this.arrivalsMonths.map((x) => x.count))
  }

  /** Origen de captación como lista de barras (no depende del browser). */
  private buildAcquisition(m: OmnichannelMetricsResponse): void {
    const acq = m?.byAcquisition ?? []
    const COLORS: Record<string, string> = { digital_organic: PALETTE.blue, referral: PALETTE.indigo, broker: PALETTE.amber, masterbroker: PALETTE.green, internal: PALETTE.navy }
    const FILLS: Record<string, string> = {
      digital_organic: 'rgba(59,130,246,0.75)',
      referral: 'rgba(99,102,241,0.75)',
      broker: 'rgba(249,115,22,0.75)',
      masterbroker: 'rgba(0,199,129,0.75)',
      internal: 'rgba(32,40,53,0.8)',
    }
    this.acquisition = acq.map((a) => ({ label: a.label, count: a.count, color: COLORS[a.key] ?? PALETTE.gray, fill: FILLS[a.key] ?? 'rgba(156,163,175,0.75)' }))
    this.acqTotal = this.acquisition.reduce((s, a) => s + a.count, 0)
    this.acqMax = Math.max(1, ...this.acquisition.map((a) => a.count))
    this.hasAcquisition = this.acqTotal > 0
  }

  /** % del total para las etiquetas del origen de captación. */
  acqPct(count: number): number {
    return this.acqTotal > 0 ? Math.round((count / this.acqTotal) * 100) : 0
  }

  /** Top referidores como lista de barras (no depende del browser). */
  private buildReferrers(m: OmnichannelMetricsResponse): void {
    this.topReferrers = m?.topReferrers ?? []
    this.hasReferrers = this.topReferrers.length > 0
    this.refMax = Math.max(1, ...this.topReferrers.map((r) => r.count))
  }

  /** Datos de las variantes Tabla + Híbrido (no dependen del browser). */
  private buildSellerData(m: OmnichannelMetricsResponse): void {
    this.sellerProspects = m?.sellerProspects ?? []
    this.sellerClients = m?.sellerClients ?? []
    this.sellerProspectDetails = m?.sellerProspectDetails ?? []
    this.hasSellerProspects = this.sellerProspects.length > 0
    this.hasSellerClients = this.sellerClients.length > 0
    this.spMax = Math.max(1, ...this.sellerProspects.map((r) => r.prospectos))
    this.scMax = Math.max(1, ...this.sellerClients.map((r) => Math.max(r.clientes, r.leases)))
  }

  /** Abre el modal de detalle con la pestaña del vendedor clicado activa. */
  openProspectDetail(sellerId: string): void {
    const idx = this.sellerProspects.findIndex((s) => s.sellerId === sellerId)
    this.detailActiveIndex = idx >= 0 ? idx : 0
    this.detailVisible = true
  }

  /** Filas de detalle (cliente/activo/valor) de un vendedor para el modal. */
  detailItems(sellerId: string): SellerProspectItem[] {
    return this.sellerProspectDetails.find((d) => d.sellerId === sellerId)?.items ?? []
  }

  /** MXN completo para el modal de detalle. */
  fmtMoney(v: number): string {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(v || 0)
  }

  /** MXN compacto ($4.85M / $120k) para las etiquetas de barra. */
  fmtCompact(v: number): string {
    const n = v || 0
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2).replace(/\.?0+$/, '')}M`
    if (n >= 1_000) return `$${Math.round(n / 1_000)}k`
    return `$${n}`
  }

  /** Rellena los últimos `n` meses (hasta el mes actual) con 0 donde no haya datos → tendencia legible. */
  private fillLastMonths(data: { month: string; count: number }[], n: number): { label: string; count: number }[] {
    const map = new Map(data.map((d) => [d.month, d.count]))
    const now = new Date()
    const out: { label: string; count: number }[] = []
    for (let i = n - 1; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
      out.push({ label: this.monthLabel(d), count: map.get(key) ?? 0 })
    }
    return out
  }

  private monthLabel(d: Date): string {
    const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
    return `${MESES[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`
  }
}
